import { Box } from "../../box";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { cssModule, isNil, isNumber, mergeProps, omitProps, resolveChildren, useAutoFocus, useChainedEventCallback, useCheckableProps, useControllableState, useForwardInputApi, useSlots } from "../../shared";
import { Span } from "../../html";
import { Text } from "../../typography";
import { VisuallyHidden } from "../../visually-hidden";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DefaultElement = "label";
export function InnerRadio(props) {
  const [checkableProps] = useCheckableProps(props);
  const {
    active,
    as = DefaultElement,
    autoFocus,
    checked,
    children,
    defaultChecked,
    disabled,
    focus,
    forwardedRef,
    hover,
    name,
    onChange,
    onCheck,
    onValueChange,
    reverse,
    tabIndex,
    validationState,
    value,
    ...rest
  } = mergeProps(omitProps(props, ["role"]), checkableProps);
  const [isChecked, setIsChecked] = useControllableState(checked, defaultChecked, false);
  const labelRef = useRef();
  const inputRef = useRef();
  useAutoFocus(inputRef, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus
  });
  const forwardInputApi = useForwardInputApi(inputRef);
  useImperativeHandle(forwardedRef, () => {
    return forwardInputApi(labelRef);
  });
  const handleStateChange = useChainedEventCallback(onChange, event => {
    const newValue = !isChecked;
    setIsChecked(newValue);

    if (!isNil(onValueChange)) {
      onValueChange(event, true);
    }
  });
  const handleCheck = useChainedEventCallback(onChange, event => {
    if (!isNil(onValueChange)) {
      onValueChange(event, true);
    }

    if (!isNil(onCheck)) {
      onCheck(event, value);
    }
  });
  const content = resolveChildren(children);
  const {
    counter,
    icon,
    text
  } = useSlots(content, useMemo(() => ({
    _: {
      defaultWrapper: Text
    },
    counter: {
      className: "o-ui-radio-counter",
      color: "inherit",
      pushed: true,
      reverse,
      variant: "divider"
    },
    icon: {
      className: "o-ui-radio-icon",
      size: "sm"
    },
    text: {
      className: "o-ui-radio-label",
      color: "inherit"
    }
  }), [reverse]));
  return /*#__PURE__*/_jsxs(Box, { ...mergeProps(rest, {
      as,
      className: cssModule("o-ui-radio", isChecked && "checked", reverse && "reverse", validationState && validationState, disabled && "disabled", active && "active", focus && "focus", hover && "hover"),
      ref: labelRef
    }),
    children: [/*#__PURE__*/_jsx(VisuallyHidden, {
      "aria-invalid": validationState === "invalid",
      as: "input",
      checked: isChecked,
      "data-type": typeof value,
      disabled: disabled,
      name: name,
      onChange: !isNil(onCheck) ? handleCheck : handleStateChange,
      ref: inputRef,
      tabIndex: tabIndex,
      type: "radio",
      value: value
    }), /*#__PURE__*/_jsx(Span, {
      className: "o-ui-radio-button"
    }), text, icon, counter]
  });
}
InnerRadio.defaultElement = DefaultElement;
export const Radio = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerRadio, { ...props,
  forwardedRef: ref
}));